import i18next from 'i18next';

const DEFAULT_HORIZONTAL_DISTANCE_IN_METERS_BETWEEN_GROUPS = 0.5;
const DEFAULT_VERTICAL_DISTANCE_IN_METERS_BETWEEN_GROUPS = 0.5;

/**
 * Systems where the roof surface is FLAT and `ceil_tilt` stores the PANEL
 * inclination above the horizontal (not a roof slope).
 * For these systems roofSlopeDeg must always be 0 and sloof_type must NOT
 * be forwarded to the 3D library (it would tilt the roof surface).
 */
export const FLAT_ROOF_SYSTEM_UIDS = [
    'ARCK-GRANJA LOSA',
    'ARCK-LASTRADA DOBLE',
    'ARCK-LASTRADA SENCILLA',
    'ARCK-LOSA CONVENCIONAL',
];

export const isFlatRoofSystem = (systemUid) =>
    FLAT_ROOF_SYSTEM_UIDS.includes(systemUid) ||
    FLAT_ROOF_SYSTEM_UIDS.includes(`ARCK-${systemUid}`);

export const getLandCategoryOptions = (system) => {
    const regions = [
        { maxSpeed: 22, value: '1' },
        { maxSpeed: 28, value: '2' },
        { maxSpeed: 35, value: '3' },
        { maxSpeed: 42, value: '4' },
        { maxSpeed: 46, value: '5' },
    ];

    const baseOptions = regions.map((region) => ({
        label: i18next.t('Region {{value}} - Up to {{maxSpeed}} m/s', {
            maxSpeed: region.maxSpeed,
            value: region.value,
        }),
        value: region.value,
    }));

    // Define allowed values per system in the specified order
    const systemTerrainMap = {};

    // Filter options based on the system's allowed values
    const systemCategory = systemTerrainMap[system] || [
        '1',
        '2',
        '3',
        '4',
        '5',
    ];
    return baseOptions.filter((option) =>
        systemCategory.includes(option.value),
    );
};

export const getStrategyOptions = () => [
    { label: i18next.t('Automatic'), value: 'automatic' },
    { label: i18next.t('Manual'), value: 'manual' },
];

export const foilGaugeOptions = [
    { label: '22', value: '22' },
    { label: '24', value: '24' },
    { label: '26', value: '26' },
];

export const getMinMaxRailSpacing = () => [2075, 6200];

export const railSpacingOptions = () => [
    { label: '2075mm', value: '2075' },
    { label: '2400mm', value: '2400' },
    { label: '3100mm', value: '3100' },
    { label: '3200mm', value: '3200' },
    { label: '3550mm', value: '3550' },
    { label: '4150mm', value: '4150' },
    { label: '4300mm', value: '4300' },
    { label: '4700mm', value: '4700' },
    { label: '5850mm', value: '5850' },
    { label: '6200mm', value: '6200' },
];

export const railSupportLengthOptions = [
    { label: '1100mm', value: '1100' },
    { label: '1300mm', value: '1300' },
    { label: '1360mm', value: '1360' },
    { label: '1400mm', value: '1400' },
    { label: '1500mm', value: '1500' },
    { label: '1600mm', value: '1600' },
];

export const plateSupportLengthOptions = [
    { label: '1100mm', value: '1100' },
    { label: '1300mm', value: '1300' },
    { label: '1360mm', value: '1360' },
    { label: '1400mm', value: '1400' },
    { label: '1500mm', value: '1500' },
    { label: '1600mm', value: '1600' },
    { label: '1700mm', value: '1700' },
];

export const beamSpacingOptions = [
    { label: '700mm', value: '700' },
    { label: '800mm', value: '800' },
    { label: '900mm', value: '900' },
    { label: '1000mm', value: '1000' },
];

export const supportLengthOptions = [
    { label: '1000mm', value: '1000' },
    { label: '1100mm', value: '1100' },
    { label: '1200mm', value: '1200' },
    { label: '1300mm', value: '1300' },
    { label: '1400mm', value: '1400' },
    { label: '1500mm', value: '1500' },
    { label: '1600mm', value: '1600' },
    { label: '1700mm', value: '1700' },
    { label: '1800mm', value: '1800' },
    { label: '1900mm', value: '1900' },
    { label: '2000mm', value: '2000' },
];

export const lFootSupportsWidthOptions = [
    { label: '1100mm', value: '1100' },
    { label: '1300mm', value: '1300' },
    { label: '1360mm', value: '1360' },
    { label: '1400mm', value: '1400' },
    { label: '1500mm', value: '1500' },
    { label: '1600mm', value: '1600' },
];

export const gClampOptions = [
    { label: 'Acesco', value: 'GCLAMP-ACESCO' },
    { label: 'Convencional', value: 'GCLAMP-CONVENTIONAL' },
    { label: 'Pestaña ancha', value: '"GCLAMP-PESTAÑA-ANCHA' },
];

export const getMinCol = (systemUid) => {
    const systems = {
        'ARCK-GRANJA LOSA': 20,
        'ARCK-LASTRADA DOBLE': 1,
        'ARCK-LASTRADA SENCILLA': 1,
        'ARCK-LOSA CONVENCIONAL': 10,
    };
    return systems[systemUid] || 10;
};

export const getMinRow = (systemUid) => {
    const systems = {
        'ARCK-GRANJA LOSA': 2,
        'ARCK-LASTRADA DOBLE': 10,
        'ARCK-LASTRADA SENCILLA': 10,
        'ARCK-LOSA CONVENCIONAL': 2,
    };
    return systems[systemUid] || 1;
};

export const getDesignCode = (systemUid) => {
    const systems = {};
    return systems[systemUid] || '';
};

export const getGroupFinalHeightInMilimeters = ({
    groupInitialHeightInMilimeters = 0,
    rows = 0,
    rowSpacingInMilimeters = 0,
    solarModuleHeightInMilimiters = 0,
    tilt = 0,
}) => {
    const groupHeight =
        rows * (solarModuleHeightInMilimiters + rowSpacingInMilimeters) -
        rowSpacingInMilimeters;
    const tiltedGroupHeight = groupHeight * Math.sin(tilt * (Math.PI / 180));
    const finalHeight = tiltedGroupHeight + groupInitialHeightInMilimeters;
    return finalHeight;
};

export const getFrameSpacingViaSystemUid = (systemUid) => {
    const defaultValues = {
        frame_col_spacing: DEFAULT_HORIZONTAL_DISTANCE_IN_METERS_BETWEEN_GROUPS,
        frame_row_spacing: DEFAULT_VERTICAL_DISTANCE_IN_METERS_BETWEEN_GROUPS,
    };
    const systems = {};
    return systems[systemUid] || defaultValues;
};

export const getSlopeOptions = (systemUid) => {
    const single = { label: i18next.t('Single slope'), value: 'single_slope' };
    const double = { label: i18next.t('Double slope'), value: 'double_slope' };
    const defaultValue = [single, double];
    const values = {
        'ARCK-LASTRADA DOBLE': [double],
        'ARCK-LASTRADA SENCILLA': [single],
    };
    return values[systemUid] || values[`ARCK-${systemUid}`] || defaultValue;
};

export const getMinMaxCeilTilt = (systemUid) => {
    const defaultValue = [5, 60];
    const values = {
        'ARCK-GRANJA LOSA': [5, 45],
        'ARCK-LASTRADA DOBLE': [10, 10],
        'ARCK-LASTRADA SENCILLA': [10, 10],
        'ARCK-LOSA CONVENCIONAL': [5, 45],
        'ARCK-TEJA BARRO': [5, 45],
        'ARCK-TRAPEZOIDAL ALT': [5, 45],
    };
    return values[systemUid] || values[`ARCK-${systemUid}`] || defaultValue;
};

export const getMinMaxBuildHeight = (systemUid) => {
    const defaultValue = [1.5, 60];
    const values = {
        'ARCK-TEJA BARRO': [1.5, 6],
    };
    return values[systemUid] || values[`ARCK-${systemUid}`] || defaultValue;
};

export const getMinMaxSeamDistance = (systemUid) => {
    const defaultValue = [1000, 2000];
    const values = {
        'ARCK-GRANJA LOSA': [0, 3800],
        'ARCK-GRANJA RIEL': [2000, 3800],
        'ARCK-LASTRADA DOBLE': [0, 0],
        'ARCK-LASTRADA SENCILLA': [0, 0],
        'ARCK-LOSA CONVENCIONAL': [0, 2000],
        'ARCK-TEJA BARRO': [1000, 1000],
    };
    return values[systemUid] || values[`ARCK-${systemUid}`] || defaultValue;
};

export const getOrientationViaSystemUid = (systemUid) => {
    const defaultValue = 'P';
    const values = {
        'ARCK-LASTRADA DOBLE': 'L',
        'ARCK-LASTRADA SENCILLA': 'L',
    };
    return values[systemUid] || values[`ARCK-${systemUid}`] || defaultValue;
};

export const getMinMaxGroupTilt = (systemUid) => {
    const defaultValue = [0, 0];
    const values = {
        'ARCK-GRANJA LOSA': [5, 45],
        'ARCK-LASTRADA DOBLE': [10, 10],
        'ARCK-LASTRADA SENCILLA': [10, 10],
        'ARCK-LOSA CONVENCIONAL': [5, 45],
    };
    return values[systemUid] || values[`ARCK-${systemUid}`] || defaultValue;
};

export const getMinMaxRows = (systemUid) => {
    const defaultValue = [1, 1];
    const values = {
        'ARCK-GRANJA LOSA': [1, 3],
        'ARCK-LASTRADA DOBLE': [1, 30],
        'ARCK-LASTRADA SENCILLA': [1, 30],
        'ARCK-LOSA CONVENCIONAL': [1, 3],
    };
    return values[systemUid] || values[`ARCK-${systemUid}`] || defaultValue;
};

export const getMinMaxCols = (systemUid) => {
    const defaultValue = [1, 25];
    const values = {
        'ARCK-GRANJA LOSA': [1, 30],
        'ARCK-GRANJA RIEL': [1, 30],
        'ARCK-LASTRADA DOBLE': [1, 1],
        'ARCK-LASTRADA SENCILLA': [1, 1],
        'ARCK-LOSA CONVENCIONAL': [1, 30],
    };
    return values[systemUid] || values[`ARCK-${systemUid}`] || defaultValue;
};

export const getSpaceBetweenModules = (systemUid) => {
    const defaultValue = { col_spacing: 0.02, row_spacing: 0.02 };
    const values = {
        'ARCK-LASTRADA DOBLE': { col_spacing: 0.04, row_spacing: 0.04 },
        'ARCK-LASTRADA SENCILLA': { col_spacing: 0.04, row_spacing: 0.04 },
    };
    return values[systemUid] || values[`ARCK-${systemUid}`] || defaultValue;
};

export const getMinMaxOuterRailLength = (systemUid) => {
    const defaultValue = [2000, 4000];
    const values = {};
    return values[systemUid] || values[`ARCK-${systemUid}`] || defaultValue;
};

export const getMinMaxFrontBaseHeight = (systemUid) => {
    const defaultValue = [200, 1700];
    const values = {};
    return values[systemUid] || values[`ARCK-${systemUid}`] || defaultValue;
};

export const getMinMaxGroupHeightInMilimeters = (systemUid) => {
    const defaultValue = [0, Infinity];
    const values = {
        'ARCK-GRANJA LOSA': [0, 1800],
        'ARCK-LASTRADA DOBLE': [0, 130],
        'ARCK-LASTRADA SENCILLA': [0, 130],
        'ARCK-LOSA CONVENCIONAL': [0, 1300],
    };
    return values[systemUid] || values[`ARCK-${systemUid}`] || defaultValue;
};

export const getFieldsToRender = (systemUid) => {
    const defaultValue = [];
    const values = {
        'ARCK-CUBIERTA GRAFADA': [
            'sloof_type',
            'ceil_tilt',
            'rail_spacing',
            'rail_support_length',
            'support_length',
            'gclamp_type',
        ],
        'ARCK-GCLAMP RAILLESS': [
            'sloof_type',
            'ceil_tilt',
            'lfoot_supports_width',
            'gclamp_type',
        ],
        'ARCK-GRANJA LOSA': [
            'outer_rail_length',
            'front_base_height',
            'rail_support_length',
        ],
        'ARCK-GRANJA RIEL': ['sloof_type', 'ceil_tilt', 'rail_support_length'],
        'ARCK-LASTRADA DOBLE': ['plate_support_length'],
        'ARCK-LASTRADA SENCILLA': ['plate_support_length'],
        'ARCK-LOSA CONVENCIONAL': [
            'rail_support_length',
            'front_base_height',
            'support_length',
        ],
        'ARCK-TEJA BARRO': [
            'sloof_type',
            'ceil_tilt',
            'rail_spacing',
            'rail_support_length',
            'beam_spacing',
        ],
        'ARCK-TRAPEZOIDAL': [
            'sloof_type',
            'rail_spacing',
            'ceil_tilt',
            'rail_support_length',
            'support_length',
        ],
        'ARCK-TRAPEZOIDAL ALT': [
            'sloof_type',
            'ceil_tilt',
            'rail_spacing',
            'rail_support_length',
            'support_length',
        ],
        'ARCK-TRAPEZOIDAL CLAMP': [
            'sloof_type',
            'ceil_tilt',
            'rail_spacing',
            'rail_support_length',
            'support_length',
        ],
        'ARCK-TRAPEZOIDAL RAILLESS': ['sloof_type', 'lfoot_supports_width'],
    };
    return values[systemUid] || values[`ARCK-${systemUid}`] || defaultValue;
};

export const normalizeRailSpacing = (railSpacing) => {
    if (!railSpacing || typeof railSpacing !== 'object') return [];

    const { value } = railSpacing;

    // Si value es un string vacío → []
    if (value === '' || value === null || value === undefined) {
        return [];
    }

    // Si value ya es un array → lo devolvemos tal cual (asumiendo que ya está bien)
    if (Array.isArray(value)) {
        return value;
    }

    // Si value es un string con número (ej: "1200")
    if (typeof value === 'string') {
        const num = Number(value.trim());
        return !isNaN(num) ? [num] : [];
    }

    // Si es un número directamente
    if (typeof value === 'number' && !isNaN(value)) {
        return [value];
    }

    // En cualquier otro caso (inválido)
    return [];
};
